/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, signal } from '@angular/core';

const STORAGE_KEY = 'aca.theme';
const DARK_CLASS = 'aca-dark-theme';

/**
 * Manages the application Light/Dark theme.
 * Adds/removes the `.aca-dark-theme` class on <body> (the SCSS dark layer is
 * scoped to that class) and persists the choice in localStorage.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Reactive flag other components can read (e.g. to swap the toggle icon). */
  readonly isDark = signal<boolean>(false);

  /** Called once at startup so the choice applies on every page (incl. login). */
  init(): void {
    const stored = localStorage.getItem(STORAGE_KEY);
    this.apply(stored === 'dark');
  }

  toggle(): void {
    this.apply(!this.isDark());
  }

  private apply(dark: boolean): void {
    this.isDark.set(dark);
    document.body.classList.toggle(DARK_CLASS, dark);
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  }
}
